/**
 * On startup, register all federated game modules
 */

import { checkForUpdate, updateApp } from '@/swRegister.js';
import { Box, Button, H2, Icon, P } from '@a-type/ui';
import { idToFederationId } from '@long-game/common';
import { GameModuleContext } from '@long-game/game-client';
import {
  emptyGameDefinition,
  GameDefinition,
} from '@long-game/game-definition';
import { DefaultChatMessage } from '@long-game/game-ui';
import { ComponentType, lazy } from 'react';
import { federation } from './federation.js';
import { publicSdk } from './publicSdk.js';

const cache: Map<string, any> = new Map();

export function getFederatedGameComponent(
  gameId: string,
  version: string,
  componentName: 'definition',
): Promise<GameDefinition>;
export function getFederatedGameComponent(
  gameId: string,
  version: string,
  componentName: 'renderer' | 'chat',
): ComponentType<any>;
export function getFederatedGameComponent(
  gameId: string,
  version: string,
  componentName: 'renderer' | 'chat' | 'definition',
): Promise<GameDefinition> | ComponentType<any> {
  if (gameId === 'empty') {
    if (componentName === 'definition') {
      return Promise.resolve(emptyGameDefinition);
    }
    if (componentName === 'renderer') {
      return lazy(() =>
        Promise.resolve({
          default: MissingRenderer,
        }),
      );
    }
    if (componentName === 'chat') {
      return lazy(() =>
        Promise.resolve({
          default: DefaultChatMessage,
        }),
      );
    }
  }
  const majorVersion = version.split('.')[0];
  const federatedPath = `${idToFederationId(gameId, majorVersion)}/${componentName}`;
  if (cache.has(federatedPath)) {
    return cache.get(federatedPath)!;
  }
  if (componentName === 'definition') {
    const promise = federation
      .loadRemote<{ default: GameDefinition }>(federatedPath)
      .then((mod) => mod?.default)
      .catch((err) => {
        console.error(err);
        return emptyGameDefinition;
      }) as Promise<GameDefinition>;
    cache.set(federatedPath, promise);
    return promise;
  }
  if (componentName === 'renderer') {
    const promise = lazy(() =>
      federation
        .loadRemote(federatedPath)
        .then((m: any) => {
          console.debug('Loaded remote renderer for', gameId, version);
          return { default: m.Renderer };
        })
        .catch((err) => {
          console.error(err);
          return {
            default: MissingRenderer,
          };
        }),
    );
    cache.set(federatedPath, promise);
    return promise;
  }
  if (componentName === 'chat') {
    const promise = lazy(() =>
      federation
        .loadRemote(federatedPath)
        .then((m: any) => ({ default: m.ChatMessage }))
        .catch((err) => {
          console.error(err);
          return {
            default: DefaultChatMessage,
          };
        }),
    );
    cache.set(federatedPath, promise);
    return promise;
  }
  throw new Error(`Unknown component name: ${componentName}`);
}

async function registerFederatedGames() {
  const games = await publicSdk.getGames.run({
    prerelease: true,
  });
  federation.registerRemotes(
    Object.entries(games)
      .map(([id, meta]) =>
        meta.versions.map((version) => ({
          name: idToFederationId(id, version.version),
          entry: `${version.url}/mf-manifest.json`,
          type: 'module',
        })),
      )
      .flat(),
  );
}

export const gamesReadyPromise = registerFederatedGames();

function MissingRenderer() {
  return (
    <Box gap col layout="center center" full>
      <Icon name="warning" size={64} />
      <H2>Game Not Found</H2>
      <P>We're having trouble loading this game.</P>
      {import.meta.env.DEV ? (
        <P>Make sure you're running the dev task for it.</P>
      ) : (
        <>
          <P>You could try reloading the app.</P>
          <Button
            onClick={async () => {
              await checkForUpdate();
              await updateApp();
              window.location.reload();
            }}
          >
            Reload
          </Button>
        </>
      )}
    </Box>
  );
}

export const gameModules: GameModuleContext = {
  getGameDefinition: (gameId, version) =>
    getFederatedGameComponent(gameId, version, 'definition'),
  getGameLatestVersion: async (gameId) => {
    const data = await publicSdk.getGame.run({ id: gameId });
    if (!data) {
      throw new Error(`Game not found: ${gameId}`);
    }
    return data.versions.at(-1)?.version ?? 'v1';
  },
};
